export const UPLOAD_PHASES = {
  IDLE: 'idle',
  COLLECTING_FEEDBACK: 'collecting_feedback',
  UPLOADING_FILE: 'uploading_file',
  WAITING_FOR_BASE_REPORT: 'waiting_for_base_report',
  BASE_REPORT_READY: 'base_report_ready',
  DONE: 'done',
  ERROR: 'error',
};

const WAITING_STAGE_TIMELINE = [
  { maxElapsedMs: 10_000, key: 'hero.upload.narrative.preparing' },
  { maxElapsedMs: 60_000, key: 'hero.upload.narrative.layout' },
  { maxElapsedMs: 150_000, key: 'hero.upload.narrative.supportingInsights' },
  { maxElapsedMs: Number.POSITIVE_INFINITY, key: 'hero.upload.narrative.finishing' },
];

export function getUploadPhaseMeta(phase) {
  switch (phase) {
    case UPLOAD_PHASES.UPLOADING_FILE:
      return {
        zoneKey: 'hero.upload.phase.uploadingFile',
        panelStageKey: 'hero.upload.phase.uploadingFile',
        tone: 'active',
        showPanel: true,
      };
    case UPLOAD_PHASES.WAITING_FOR_BASE_REPORT:
      return {
        zoneKey: 'hero.upload.phase.waitingForBaseReport',
        panelStageKey: 'hero.upload.phase.waitingForBaseReport',
        tone: 'active',
        showPanel: true,
      };
    case UPLOAD_PHASES.BASE_REPORT_READY:
    case UPLOAD_PHASES.DONE:
      return {
        zoneKey: 'hero.upload.phase.baseReportReady',
        panelStageKey: 'hero.upload.phase.baseReportReady',
        tone: 'success',
        showPanel: true,
      };
    case UPLOAD_PHASES.ERROR:
      return {
        zoneKey: 'hero.upload.phase.error',
        panelStageKey: 'hero.upload.phase.error',
        tone: 'error',
        showPanel: true,
      };
    case UPLOAD_PHASES.COLLECTING_FEEDBACK:
      return {
        zoneKey: 'hero.upload.phase.collectingFeedback',
        panelStageKey: null,
        tone: 'idle',
        showPanel: false,
      };
    case UPLOAD_PHASES.IDLE:
    default:
      return {
        zoneKey: 'hero.upload.dragDrop',
        panelStageKey: null,
        tone: 'idle',
        showPanel: false,
      };
  }
}

export function getUploadNarrativeStageKey(phase, elapsedMs = 0) {
  if (phase === UPLOAD_PHASES.WAITING_FOR_BASE_REPORT) {
    return WAITING_STAGE_TIMELINE.find((stage) => elapsedMs <= stage.maxElapsedMs)?.key
      || WAITING_STAGE_TIMELINE[WAITING_STAGE_TIMELINE.length - 1].key;
  }

  return getUploadPhaseMeta(phase).panelStageKey;
}

export function getUploadProgressValue(phase, elapsedMs = 0) {
  switch (phase) {
    case UPLOAD_PHASES.UPLOADING_FILE:
      return Math.min(12, Math.floor(elapsedMs / 500));
    case UPLOAD_PHASES.WAITING_FOR_BASE_REPORT: {
      if (elapsedMs <= 10_000) {
        return Math.floor((elapsedMs / 10_000) * 20);
      }
      if (elapsedMs <= 60_000) {
        return 20 + Math.floor(((elapsedMs - 10_000) / 50_000) * 24);
      }
      if (elapsedMs <= 150_000) {
        return 44 + Math.floor(((elapsedMs - 60_000) / 90_000) * 30);
      }
      if (elapsedMs <= 240_000) {
        return 74 + Math.floor(((elapsedMs - 150_000) / 90_000) * 12);
      }
      if (elapsedMs <= 300_000) {
        return 86 + Math.floor(((elapsedMs - 240_000) / 60_000) * 4);
      }
      return 90;
    }
    case UPLOAD_PHASES.BASE_REPORT_READY:
    case UPLOAD_PHASES.DONE:
      return 100;
    case UPLOAD_PHASES.IDLE:
    case UPLOAD_PHASES.COLLECTING_FEEDBACK:
    case UPLOAD_PHASES.ERROR:
    default:
      return null;
  }
}

export function getAnalysisReadiness({
  analysis,
  roomAnalysis,
  costAnalysis,
  evaluation,
  userIntent,
}) {
  if (!analysis) {
    return {
      baseReportReady: false,
      rooms: 'idle',
      costs: 'idle',
      requirements: 'idle',
      improvements: 'idle',
    };
  }

  const hasRequirementsIntent = Boolean(userIntent?.expectations?.trim());

  return {
    baseReportReady: true,
    rooms: roomAnalysis ? 'ready' : 'loading',
    costs: costAnalysis ? 'ready' : 'loading',
    requirements: hasRequirementsIntent ? (evaluation ? 'ready' : 'loading') : 'idle',
    improvements: 'ready',
  };
}
